/**
 * Core type definitions for the 2D rocket landing physics engine.
 *
 * Coordinate system (inertial, world frame):
 *   X: horizontal, right positive (downrange)
 *   Z: vertical, up positive (altitude), ground = 0
 *   Origin: launch pad / landing target
 *
 * Body frame (2D):
 *   theta: pitch angle measured from +Z toward +X
 *   theta = 0: rocket points straight up
 *   theta > 0: nose tilted toward +X
 */

export type Vector2 = [number, number];

export interface RocketState {
  /** Simulation time (s). */
  readonly t: number;
  /** Inertial horizontal position (m). */
  readonly x: number;
  /** Inertial altitude (m), ground = 0. */
  readonly z: number;
  /** Inertial horizontal velocity (m/s). */
  readonly vx: number;
  /** Inertial vertical velocity (m/s). */
  readonly vz: number;
  /** Remaining propellant (kg). */
  readonly fuel: number;
  /** Pitch angle from vertical toward +X (rad). */
  readonly theta: number;
  /** Pitch rate (rad/s). */
  readonly omega: number;
}

export interface ControlAction {
  /** Commanded throttle fraction before fuel/exhaustion clamping (0 .. 1). */
  readonly throttle: number;
  /** Commanded thrust vector gimbal deflection (rad). */
  readonly gimbal: number;
}

export interface Environment {
  /** Gravitational acceleration (m/s²). */
  readonly gravity: number;
  /** Sea-level atmospheric density (kg/m³). */
  readonly seaLevelDensity: number;
  /** Atmospheric scale height (m). Infinity = constant density. */
  readonly scaleHeight: number;
  /**
   * Atmospheric density at altitude z (m), in kg/m³.
   * Implemented by EnvironmentModel.
   */
  readonly density: (z: number) => number;
}

export interface RocketParams {
  /** Dry rocket mass without propellant (kg). */
  readonly dryMass: number;
  /** Maximum propellant capacity (kg). */
  readonly maxFuel: number;
  /** Maximum thrust force (N). */
  readonly maxThrust: number;
  /** Propellant mass flow rate at full throttle (kg/s). */
  readonly fuelFlowRate: number;
  /** Dimensionless drag coefficient. */
  readonly dragCoefficient: number;
  /** Reference area for drag calculation (m²). */
  readonly referenceArea: number;
  /** Pitch moment of inertia about center of mass (kg·m²). */
  readonly inertiaY: number;
  /** Distance from center of mass to engine mount (m). */
  readonly centerOfMassOffset: number;
}

export interface SimulationConfig {
  readonly environment: Environment;
  readonly rocket: RocketParams;
  readonly initialState: RocketState;
  /** Maximum gimbal deflection (rad). */
  readonly maxGimbalAngle: number;
  /** Physics integration timestep (s). */
  readonly physicsDt: number;
  /** Telemetry output interval (s), must be integer multiple of physicsDt. */
  readonly outputDt: number;
  /** Maximum simulation duration (s). */
  readonly maxDuration: number;
  /** Landing target horizontal position (m). */
  readonly landingTargetX: number;
  /** Maximum allowed vertical touchdown speed (m/s). */
  readonly maxTouchdownVerticalSpeed: number;
  /** Maximum allowed horizontal touchdown speed (m/s). */
  readonly maxTouchdownHorizontalSpeed: number;
  /** Maximum allowed horizontal distance from landing target at touchdown (m). */
  readonly maxHorizontalError: number;
  /** Maximum allowed attitude at touchdown (rad). */
  readonly maxTouchdownAttitude: number;
  /** Maximum allowed angular rate at touchdown (rad/s). */
  readonly maxTouchdownAngularRate: number;
  /** Descent rate magnitude before flare (m/s). */
  readonly descentRate: number;
  /** Altitude where flare to zero vertical velocity begins (m). */
  readonly flareAltitude: number;
}

export interface TelemetryPoint {
  readonly t: number;
  readonly x: number;
  readonly z: number;
  readonly vx: number;
  readonly vz: number;
  /** Magnitude of velocity vector (m/s). */
  readonly speed: number;
  readonly altitude: number;
  readonly fuel: number;
  /** Total mass = dryMass + fuel (kg). */
  readonly mass: number;
  /** Commanded throttle from controller (0 .. 1). */
  readonly throttle: number;
  /** Applied throttle after fuel-exhaustion clamping (0 .. 1). */
  readonly effectiveThrottle: number;
  readonly gimbal: number;
  readonly theta: number;
  readonly omega: number;
  /** Horizontal acceleration component (m/s²). */
  readonly ax: number;
  /** Vertical acceleration component (m/s²). */
  readonly az: number;
  /** Magnitude of acceleration vector (m/s²). */
  readonly accelMagnitude: number;
}

export interface LandingResult {
  readonly landingSuccess: boolean;
  readonly touchdownVx: number;
  readonly touchdownVz: number;
  /** Magnitude of touchdown velocity (m/s). */
  readonly touchdownSpeed: number;
  /** Horizontal distance from landing target at touchdown (m). */
  readonly horizontalError: number;
  readonly impactTime: number;
}

export interface SimulationResult {
  readonly telemetry: readonly TelemetryPoint[];
  readonly finalState: RocketState | null;
  readonly landing: LandingResult;
}

export interface Controller {
  readonly name: string;
  reset(): void;
  compute(state: RocketState, env: Environment, dt: number): ControlAction;
}

export function isFiniteState(state: RocketState): boolean {
  return (
    Number.isFinite(state.t) &&
    Number.isFinite(state.x) &&
    Number.isFinite(state.z) &&
    Number.isFinite(state.vx) &&
    Number.isFinite(state.vz) &&
    Number.isFinite(state.fuel) &&
    Number.isFinite(state.theta) &&
    Number.isFinite(state.omega)
  );
}

export function isFiniteAction(action: ControlAction): boolean {
  return Number.isFinite(action.throttle) && Number.isFinite(action.gimbal);
}
